import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import useStoreSubdomain from "@/hooks/useStoreSubdomain";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, CheckCircle, Loader2 } from "lucide-react";
import { getWhitelabelConfig } from "@/lib/whitelabel";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function LeadForm() {
  const { tenantId: paramTenantId } = useParams();
  const subdomain = useStoreSubdomain();
  const identifier = paramTenantId || subdomain;
  const [config, setConfig] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [form, setForm] = useState({
    name: "", phone: "", email: "", product_interest: "", budget: "", notes: ""
  });
  const [customValues, setCustomValues] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    fetchConfig();
  }, [identifier]);

  const fetchConfig = async () => {
    try {
      let tenantId = identifier;

      // If identifier is not a UUID, resolve it via store API first
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(identifier);
      if (!isUUID) {
        const tenantRes = await fetch(`${BACKEND}/api/public/store/${identifier}`);
        if (!tenantRes.ok) { setError("Toko tidak ditemukan"); setLoading(false); return; }
        const tenantData = await tenantRes.json();
        tenantId = tenantData.id || tenantData.tenant_id;
        setTenant(tenantData);
      }

      const res = await fetch(`${BACKEND}/api/public/lead-form/${tenantId}`);
      if (!res.ok) { setError("Form tidak tersedia"); setLoading(false); return; }
      const data = await res.json();
      if (!data.enabled) { setError("Form sedang tidak aktif"); setLoading(false); return; }
      setConfig({ ...data, tenant_id: tenantId });
    } catch {
      setError("Gagal memuat form");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!config) return;

    const required = config.required_fields || ["name", "phone"];
    for (const field of required) {
      if (!form[field]?.trim()) {
        setError(`${field === "name" ? "Nama" : field === "phone" ? "Telepon" : field === "email" ? "Email" : field === "product_interest" ? "Minat produk" : "Budget"} wajib diisi`);
        return;
      }
    }

    setSubmitting(true);
    setError("");
    try {
      const body = {
        name: form.name,
        phone: form.phone || null,
        email: form.email || null,
        product_interest: form.product_interest || null,
        budget: form.budget ? parseFloat(form.budget) : null,
        notes: form.notes || null,
        source: "form",
        custom_fields: Object.keys(customValues).length > 0 ? customValues : null
      };
      const res = await fetch(`${BACKEND}/api/public/leads/${config.tenant_id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error("Submit failed");
      const data = await res.json();
      setSuccessMsg(data.message || config.success_message || "Terima kasih!");
      setSubmitted(true);
    } catch {
      setError("Gagal mengirim data. Silakan coba lagi.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error && !config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-lg text-gray-600 mb-4">{error}</p>
          <Link to="/" className="text-blue-600 hover:underline text-sm">Kembali ke beranda</Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Berhasil!</h2>
          <p className="text-gray-600">{successMsg}</p>
        </div>
      </div>
    );
  }

  const wl = getWhitelabelConfig(tenant);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-xl shadow-lg p-8">
        {/* Header */}
        {config?.tenant_logo && (
          <img src={config.tenant_logo} alt="" className="h-10 mx-auto mb-4 object-contain" />
        )}
        <h1 className="text-2xl font-bold text-center mb-1">{config?.title || "Hubungi Kami"}</h1>
        {config?.tenant_name && (
          <p className="text-center text-sm text-gray-500 mb-1">{config.tenant_name}</p>
        )}
        {config?.description && (
          <p className="text-center text-gray-600 text-sm mb-6">{config.description}</p>
        )}

        {error && (
          <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg mb-4">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Nama {(config?.required_fields || []).includes("name") && <span className="text-red-500">*</span>}
            </label>
            <Input placeholder="Nama lengkap" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              No. Telepon {(config?.required_fields || []).includes("phone") && <span className="text-red-500">*</span>}
            </label>
            <Input placeholder="08xxxxxxxxxx" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Email {(config?.required_fields || []).includes("email") && <span className="text-red-500">*</span>}
            </label>
            <Input type="email" placeholder="email@contoh.com" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Minat Produk/Jasa {(config?.required_fields || []).includes("product_interest") && <span className="text-red-500">*</span>}
            </label>
            <Input placeholder="Apa yang Anda cari?" value={form.product_interest} onChange={e => setForm(p => ({ ...p, product_interest: e.target.value }))} />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Budget {(config?.required_fields || []).includes("budget") && <span className="text-red-500">*</span>}
            </label>
            <Input type="number" placeholder="Rp" value={form.budget} onChange={e => setForm(p => ({ ...p, budget: e.target.value }))} />
          </div>

          {/* Custom Fields */}
          {(config?.custom_fields || []).map((cf, i) => (
            <div key={i}>
              <label className="block text-sm font-medium mb-1">
                {cf.label || cf.name} {cf.required && <span className="text-red-500">*</span>}
              </label>
              {cf.type === "textarea" ? (
                <Textarea placeholder={cf.label} value={customValues[cf.name] || ""}
                  onChange={e => setCustomValues(p => ({ ...p, [cf.name]: e.target.value }))} />
              ) : (
                <Input type={cf.type === "number" ? "number" : "text"} placeholder={cf.label}
                  value={customValues[cf.name] || ""}
                  onChange={e => setCustomValues(p => ({ ...p, [cf.name]: e.target.value }))} />
              )}
            </div>
          ))}

          <div>
            <label className="block text-sm font-medium mb-1">Catatan</label>
            <Textarea placeholder="Catatan tambahan..." value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={3} />
          </div>

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Mengirim...</> : "Kirim"}
          </Button>
        </form>

        {(wl.footerText || !wl.hidePoweredBy) && (
          <p className="text-xs text-center text-gray-400 mt-6">
            {wl.footerText || 'Powered by Tokomoe'}
          </p>
        )}
      </div>
    </div>
  );
}
